import { Link } from 'react-router-dom';
import { adminApi } from '../services/admin';
import { useAsync } from '../hooks/useAsync';
import { Eyebrow, EmptyState, Loading, Panel, StatCard, StatusBadge, Tag } from '../components/ui';
import { ProviderDonut, UsageAreaChart } from '../components/charts';
import { ConversionPanel } from '../components/ConversionPanel';
import { fmtCompact, fmtInt, fmtUsd, relativeTime } from '../lib/format';
import type { AiJobStatus } from '@chefty/shared-types';

const STATUS_ORDER: AiJobStatus[] = [
  'completed',
  'needs_review',
  'processing',
  'pending',
  'failed',
  'cancelled',
];

export const OverviewPage: React.FC = () => {
  const stats = useAsync(() => adminApi.stats(), []);
  const usage = useAsync(() => adminApi.usage(30), []);
  const recent = useAsync(() => adminApi.jobs({ pageSize: 6 }), []);

  if (stats.loading && !stats.data) return <Loading label="Loading metrics…" />;
  if (stats.error) return <EmptyState title="Couldn’t load metrics" hint={stats.error} />;
  const s = stats.data!;

  const byStatus = new Map(s.jobsByStatus.map((j) => [j.status, j.count]));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard label="Users" value={fmtInt(s.totalUsers)} accent="teal" delay={0} />
        <StatCard label="Recipes" value={fmtInt(s.totalRecipes)} accent="teal" delay={40} />
        <StatCard label="Import jobs" value={fmtInt(s.totalImportJobs)} accent="cream" delay={80} />
        <StatCard
          label="AI tokens"
          value={fmtCompact(s.totalTokens)}
          sub={`${fmtInt(s.totalTokens)} total`}
          delay={120}
        />
        <StatCard
          label="Est. AI spend"
          value={fmtUsd(s.totalEstimatedCostUsd)}
          sub="frozen at call time"
          delay={160}
        />
      </div>

      <ConversionPanel />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel className="rise lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <Eyebrow>Token usage</Eyebrow>
              <h2 className="font-display text-lg text-cream-50">Last 30 days</h2>
            </div>
          </div>
          {usage.loading && !usage.data ? (
            <Loading />
          ) : usage.data && usage.data.series.length ? (
            <UsageAreaChart series={usage.data.series} />
          ) : (
            <EmptyState title="No usage yet" hint="Token accounting starts from first import." />
          )}
        </Panel>

        <Panel className="rise">
          <div className="mb-4">
            <Eyebrow>By provider</Eyebrow>
            <h2 className="font-display text-lg text-cream-50">Token share</h2>
          </div>
          <ProviderDonut data={s.providerBreakdown} />
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel className="rise">
          <Eyebrow>Import jobs by status</Eyebrow>
          <ul className="mt-4 space-y-3">
            {STATUS_ORDER.map((st) => (
              <li key={st} className="flex items-center justify-between">
                <StatusBadge status={st} />
                <span className="tnum text-sm text-cream-200">{fmtInt(byStatus.get(st) ?? 0)}</span>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel className="rise lg:col-span-2" pad={false}>
          <div className="flex items-center justify-between px-5 pt-5">
            <div>
              <Eyebrow>Activity</Eyebrow>
              <h2 className="font-display text-lg text-cream-50">Recent import jobs</h2>
            </div>
            <Link to="/worker" className="text-xs font-medium text-saffron-300 hover:text-saffron-400">
              View worker →
            </Link>
          </div>
          <div className="mt-3 divide-y divide-cream-600/10">
            {recent.data?.items.length ? (
              recent.data.items.map((j) => (
                <div key={j.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <Tag>{j.sourceType}</Tag>
                    <span className="truncate text-sm text-cream-300" title={j.userEmail ?? j.userId}>
                      {j.userEmail ?? j.userId}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-4">
                    <StatusBadge status={j.status} />
                    <span className="tnum w-16 text-right text-xs text-cream-400">
                      {relativeTime(j.createdAt)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState title="No import jobs yet" />
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
};
